import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { ColumnCandidate } from 'pages/contract/PasteDefinitionManagement';

export type PasteDefinitionFormHandle = {
  submit: () => void;
};

type PasteDefinitionFormProps = {
  mode: 'create' | 'edit';
  initialName?: string;
  initialColumns?: string[];
  columnCandidates: ColumnCandidate[];
  onSubmit: (name: string, columns: string[]) => void;
  onCancel?: () => void;
};

const PasteDefinitionForm = forwardRef<PasteDefinitionFormHandle, PasteDefinitionFormProps>(
  ({ mode, initialName = '', initialColumns = [], columnCandidates, onSubmit }, ref) => {
    const [name, setName] = useState(initialName);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(initialColumns);
    const [filterText, setFilterText] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});

    // D&D state for right list reordering only
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dropIndex, setDropIndex] = useState<number | null>(null);

    const selectedSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);

    const filteredCandidates = useMemo(() => {
      if (!filterText) return columnCandidates;
      const lower = filterText.toLowerCase();
      return columnCandidates.filter((c) => c.label.toLowerCase().includes(lower));
    }, [columnCandidates, filterText]);

    // Get label for a column key
    const getLabel = useCallback(
      (key: string) => {
        const found = columnCandidates.find((c) => c.key === key);
        return found ? found.label : key;
      },
      [columnCandidates],
    );

    // Add column to end (button click)
    const handleAddColumn = useCallback((key: string) => {
      setSelectedColumns((prev) => {
        if (prev.includes(key)) return prev;
        return [...prev, key];
      });
    }, []);

    // Remove column
    const handleRemoveColumn = useCallback((key: string) => {
      setSelectedColumns((prev) => prev.filter((k) => k !== key));
    }, []);

    // --- Right list D&D (reorder) ---
    const handleDragStart = (e: React.DragEvent, index: number) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
      setDragIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIndex !== null && dragIndex !== index) {
        setDropIndex(index);
      }
    };

    const handleDrop = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === index) {
        setDragIndex(null);
        setDropIndex(null);
        return;
      }

      setSelectedColumns((prev) => {
        const newArr = [...prev];
        const [removed] = newArr.splice(dragIndex, 1);
        const insertAt = index > dragIndex ? index - 1 : index;
        newArr.splice(insertAt, 0, removed);
        return newArr;
      });
      setDragIndex(null);
      setDropIndex(null);
    };

    const handleDragEnd = () => {
      setDragIndex(null);
      setDropIndex(null);
    };

    // Validation
    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!name.trim()) {
        newErrors.name = MSG_REQUIRED('ペースト名');
      } else if (name.trim().length > 200) {
        newErrors.name = MSG_MAX_LENGTH('ペースト名', 200);
      }

      if (selectedColumns.length === 0) {
        newErrors.columns = 'カラムを1つ以上選択してください。';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Expose submit via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (!validate()) return;
          onSubmit(name.trim(), selectedColumns);
        },
      }),
      [name, selectedColumns, onSubmit],
    );

    return (
      <Stack spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
        {/* ヘッダー */}
        <Typography variant="subtitle1" fontWeight={600}>
          {mode === 'create' ? 'ペースト定義を作成' : 'ペースト定義を編集'}
        </Typography>

        {/* ペースト名 */}
        <TextField
          label="ペースト名"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          required
          error={!!errors.name}
          helperText={errors.name}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        {/* Transfer List */}
        {errors.columns && (
          <Typography variant="caption" color="error.main">
            {errors.columns}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minHeight: 0 }}>
          {/* 左リスト（候補） */}
          <Box
            sx={{
              flex: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* フィルタ */}
            <Box sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="カラムを検索..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.85rem',
                  },
                }}
              />
            </Box>
            {/* 候補リスト */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {filteredCandidates.map((candidate) => {
                const isAlreadySelected = selectedSet.has(candidate.key);
                return (
                  <Box
                    key={candidate.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 0.75,
                      gap: 1,
                      opacity: isAlreadySelected ? 0.4 : 1,
                      pointerEvents: isAlreadySelected ? 'none' : 'auto',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: isAlreadySelected ? 'transparent' : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {candidate.label}
                    </Typography>
                    {!isAlreadySelected && (
                      <IconButton
                        size="small"
                        onClick={() => handleAddColumn(candidate.key)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
              {filteredCandidates.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    該当なし
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* 右リスト（選択済み） */}
          <Box
            sx={{
              flex: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* ヘッダー */}
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                選択済み（{selectedColumns.length}列）
              </Typography>
            </Box>
            {/* 選択済みリスト */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {selectedColumns.map((key, index) => (
                <Box
                  key={key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    gap: 0.5,
                    cursor: 'grab',
                    bgcolor: dropIndex === index ? 'action.selected' : 'transparent',
                    opacity: dragIndex === index ? 0.5 : 1,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderTop: dropIndex === index ? '2px solid' : '2px solid transparent',
                    borderTopColor: dropIndex === index ? 'primary.main' : 'transparent',
                  }}
                >
                  <DragIndicatorIcon
                    sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {getLabel(key)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveColumn(key)}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
              {selectedColumns.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    左のリストからカラムを追加してください
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  },
);

PasteDefinitionForm.displayName = 'PasteDefinitionForm';

export default PasteDefinitionForm;
